import React, { useState, useEffect } from 'react';
import { X, Search, Plus, Package2, ChevronDown } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { getStockItems, StockItem, createStockTransaction } from '../lib/stockItems';

interface ReceiveStockModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ReceiveStockModal({
  isOpen,
  onClose,
  onSuccess
}: ReceiveStockModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<StockItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<StockItem | null>(null);
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadItems();
    }
  }, [isOpen]);

  const loadItems = async () => {
    if (!selectedBusiness?.id) return;

    try {
      const data = await getStockItems(selectedBusiness.id);
      setItems(data);
    } catch (err) {
      console.error('Error loading items:', err);
      setError(err instanceof Error ? err.message : 'Failed to load items');
    }
  };

  // Filter items based on search query
  const filteredItems = React.useMemo(() => {
    if (!searchQuery) return [];
    const searchLower = searchQuery.toLowerCase();
    return items.filter(item => 
      !item.is_deleted &&
      (item.name.toLowerCase().includes(searchLower) ||
      item.description?.toLowerCase().includes(searchLower) ||
      item.sku?.toLowerCase().includes(searchLower))
    );
  }, [items, searchQuery]);

  // Group items by category
  const itemsByCategory = React.useMemo(() => {
    // First filter out deleted items
    const activeItems = items.filter(item => !item.is_deleted);
    
    const grouped = activeItems.reduce((acc, item) => {
      const categoryId = item.category_id || 'uncategorized';
      if (!acc[categoryId]) {
        acc[categoryId] = {
          id: categoryId,
          name: item.category?.name || 'Uncategorized',
          items: []
        };
      }
      acc[categoryId].items.push(item);
      return acc;
    }, {} as Record<string, { id: string; name: string; items: StockItem[] }>);

    return Object.values(grouped).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  // Filter categories based on search query
  const filteredCategories = itemsByCategory.filter(category => {
    const searchLower = searchQuery.toLowerCase();
    return (
      category.name.toLowerCase().includes(searchLower) ||
      category.items.some(item => 
        item.name.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower) ||
        item.sku?.toLowerCase().includes(searchLower)
      )
    );
  });

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setSearchQuery('');
      setSelectedItem(null);
      setQuantity('');
      setPrice('');
      setNotes('');
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem) {
      setError('Please select an item');
      return;
    }

    const receiveQuantity = parseInt(quantity);
    if (isNaN(receiveQuantity) || receiveQuantity <= 0) {
      setError('Please enter a valid quantity');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await createStockTransaction({
          item_id: selectedItem.id,
          type: 'purchase',
          quantity: receiveQuantity,
          price: parseFloat(price) || selectedItem.acquisition_cost,
          notes: notes || null
      });

      onSuccess();
    } catch (err) {
      console.error('Error receiving stock:', err);
      setError(err instanceof Error ? err.message : 'Failed to receive stock');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[10000]`}
      onClick={onClose}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-[#2f2f2f] bg-[#1a1a1a] flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Receive Stock</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
            <div className="space-y-8">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              {/* Item Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Item
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search items..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 text-sm bg-gray-50 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>

                {/* Show search results directly if searching */}
                {searchQuery ? (
                  <div className="mt-4 space-y-2 bg-white rounded-lg border border-gray-200">
                    {filteredItems.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        No items match your search
                      </div>
                    ) : (
                      filteredItems.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => {
                            setSelectedItem(item);
                            setPrice(item.acquisition_cost.toString());
                          }}
                          className={`w-full flex items-center p-4 ${
                            selectedItem?.id === item.id
                              ? 'bg-gray-50'
                              : 'hover:bg-gray-50'
                          } ${
                            item.quantity <= item.reorder_point ? 'bg-red-50' : ''
                          }`}
                        >
                          <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center mr-4">
                            <Package2 className="h-5 w-5 text-gray-400" />
                          </div>
                          <div className="flex-1 text-left">
                            <div className="text-sm font-medium text-gray-900">
                              {item.name}
                              <span className="ml-2 text-xs text-gray-500">
                                ({item.category?.name || 'Uncategorized'})
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              Current Stock: {item.quantity} {item.unit}
                              {item.quantity <= item.reorder_point && (
                                <span className="text-red-600 font-medium ml-2">
                                  (Below reorder point)
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                ) : (
                  <div className="mt-4 space-y-4">
                  {filteredCategories.map((category) => (
                    <div key={category.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleCategory(category.id)}
                        className={`w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 ${
                          category.items.some(item => item.quantity <= item.reorder_point) 
                            ? 'bg-red-50' 
                            : 'bg-green-50'
                        }`}
                      >
                        <div className="flex items-center">
                          <ChevronDown 
                            className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                              expandedCategories.has(category.id) ? 'rotate-180' : ''
                            }`}
                          />
                          <span className={`ml-2 text-sm font-medium ${
                            category.items.some(item => item.quantity <= item.reorder_point)
                              ? 'text-red-900'
                              : 'text-green-900'
                          }`}>
                            {category.name}
                          </span>
                          <span className="ml-2 text-xs text-gray-500">
                            ({category.items.length})
                          </span>
                          {category.items.some(item => item.quantity <= item.reorder_point) && (
                            <span className="ml-2 text-xs text-red-600 font-medium">
                              Low Stock
                            </span>
                          )}
                        </div>
                      </button>

                      {expandedCategories.has(category.id) && (
                        <div className="bg-white border-t border-gray-200 divide-y divide-gray-200">
                          {category.items.map((item) => (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => {
                                setSelectedItem(item);
                                setPrice(item.acquisition_cost.toString());
                              }}
                              className={`w-full flex items-center p-4 ${
                                selectedItem?.id === item.id
                                  ? 'bg-gray-50'
                                  : 'hover:bg-gray-50'
                              } ${
                                item.quantity <= item.reorder_point ? 'bg-red-50' : ''
                              }`}
                            >
                              <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center mr-4">
                                <Package2 className="h-5 w-5 text-gray-400" />
                              </div>
                              <div className="flex-1 text-left">
                                <div className="text-sm font-medium text-gray-900">{item.name}</div>
                                <div className="text-xs text-gray-500 mt-0.5">
                                  Current Stock: {item.quantity} {item.unit}
                                  {item.quantity <= item.reorder_point && (
                                    <span className="text-red-600 font-medium ml-2">
                                      (Below reorder point)
                                    </span>
                                  )}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  </div>
                )}
              </div>

              {selectedItem && (
                <>
                  {/* Quantity */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quantity to Receive
                    </label>
                    <input
                      type="number"
                      required
                      min="1"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                      placeholder={`Enter quantity in ${selectedItem.unit}`}
                    />
                  </div>

                  {/* Price */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Unit Cost
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        required
                        step="0.01"
                        min="0"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        className="w-full pl-7 pr-4 h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                      placeholder="Add any notes about this stock receipt..."
                    />
                  </div>

                  {/* Total */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Total Cost:</span>
                      <span className="text-lg font-medium text-gray-900">
                        ${((parseFloat(price) || 0) * (parseInt(quantity) || 0)).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="mt-8">
              <button
                type="submit"
                disabled={loading || !selectedItem || !quantity || !price}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Receiving...' : 'Receive Stock'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}